import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';
import { identity, tofu } from '../services/tunnel';

const AuthContext = createContext(null);

// ADR-014: make this device's identity key discoverable to friends. Publishing
// is best-effort and purely for showing fingerprints — a call authenticates on
// the key exchanged in-band, never on what this endpoint returns.
function publishIdentity(user) {
  try {
    // Point the stores at this account BEFORE reading a key out of them.
    // Unscoped, a shared browser handed the next person to sign in the
    // previous one's device identity and every peer they had pinned.
    identity.setAccount(user?.id ?? null);
    tofu.setAccount(user?.id ?? null);
    const label = /iPhone|iPad/.test(navigator.userAgent) ? 'iOS'
      : /Android/.test(navigator.userAgent) ? 'Android' : 'Web';
    api.publishIdentityKey(identity.publicKeyB64(), label).catch(() => {});
  } catch (e) { /* never block sign-in on this */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('voxiox_token');
    if (token) {
      api.me().then((u) => { setUser(u); publishIdentity(u); }).catch((err) => {
        // Only clear token on auth errors (401/403), not network errors.
        // Network errors happen during server deploys — token may still be valid.
        if (err.status === 401 || err.status === 403) {
          localStorage.removeItem('voxiox_token');
        } else {
          // Keep token, retry once after 2s (server might be restarting)
          setTimeout(() => {
            api.me().then((u) => { setUser(u); publishIdentity(u); }).catch(() => {
              localStorage.removeItem('voxiox_token');
            });
          }, 2000);
        }
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    const data = await api.login(username, password);
    localStorage.setItem('voxiox_token', data.token);
    setUser(data.user);
    publishIdentity(data.user);
    return { token: data.token, user: data.user };
  };

  const loginWithToken = (token, userData) => {
    localStorage.setItem('voxiox_token', token);
    setUser(userData);
    publishIdentity(userData);
  };

  const logout = () => {
    // Let go of this account's keys and pins. They stay on disk under their
    // own scope; the next person to sign in simply cannot see them.
    identity.setAccount(null);
    tofu.setAccount(null);
    localStorage.removeItem('voxiox_token');
    // Don't clear Keychain token — it's needed for Face ID re-login.
    // Keychain is only cleared when user explicitly unlinks Face ID in settings,
    // or when the token is expired (handled in Login.jsx).
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, loginWithToken, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
